import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import RecipeCard from "../components/RecipeCard";
import ShareCookbookDialog from "../components/ShareCookbookDialog";
import { Loader2, Share2 } from "lucide-react";

const Cookbook = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/");
      return;
    }
    setLoading(true);
    api
      .get("/favorites")
      .then((res) => setRecipes(res.data))
      .catch(() => setRecipes([]))
      .finally(() => setLoading(false));
  }, [user, authLoading, navigate]);

  return (
    <div className="min-h-screen pt-24 pb-32 bg-stone-950">
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10">
        <div className="mb-16 border-b border-stone-800 pb-12">
          <div className="flex items-center gap-4 mb-6">
            <span className="magazine-number text-5xl">05</span>
            <div className="w-16 h-px bg-orange-600" />
            <span className="eyebrow">Your Cookbook</span>
          </div>
          <div className="flex items-end justify-between gap-8 flex-wrap">
            <h1 className="font-serif-display text-6xl md:text-7xl text-stone-100 leading-[0.9] tracking-tighter">
              Your greatest <span className="italic text-orange-600">hits</span>.
            </h1>
            {recipes.length > 0 && (
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  data-testid="cookbook-share-btn"
                  className="group inline-flex items-center gap-3 bg-orange-700 hover:bg-orange-800 text-stone-50 px-6 py-3.5 uppercase tracking-[0.24em] text-xs font-medium transition-colors"
                >
                  <Share2 className="w-3.5 h-3.5" />
                  Share this cookbook
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/cookbook/manage")}
                  data-testid="cookbook-manage-btn"
                  className="text-xs uppercase tracking-[0.24em] text-stone-400 hover:text-orange-500 ink-link"
                >
                  Manage shares →
                </button>
              </div>
            )}
          </div>
          {recipes.length > 0 && (
            <p className="mt-6 max-w-xl text-sm text-stone-400 leading-relaxed">
              {recipes.length} recipe{recipes.length === 1 ? "" : "s"} on the shelf. Bundle
              the lot into a single shareable link with a proper cover.
            </p>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-32">
            <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
          </div>
        ) : recipes.length === 0 ? (
          <div className="text-center py-32 border border-stone-800">
            <div className="magazine-number text-8xl mb-6">Ø</div>
            <p className="font-serif-display italic text-2xl text-stone-400 mb-6">
              Nothing in the cookbook yet. Go have a rummage.
            </p>
            <button
              onClick={() => navigate("/library")}
              data-testid="cookbook-browse"
              className="text-orange-500 uppercase text-xs tracking-[0.24em] ink-link"
            >
              Rummage the library
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {recipes.map((r, i) => (
              <RecipeCard key={r.id} recipe={r} index={i} />
            ))}
          </div>
        )}
      </div>
      <ShareCookbookDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        defaultTitle={user ? `${user.display_name}'s cookbook` : ""}
      />
    </div>
  );
};

export default Cookbook;
